import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapPin, Search, ChevronDown, X, Loader2, Filter, Scale, CheckSquare, Square } from 'lucide-react';
import type { FUNAITerritoryIndex, FUNAITerritoryEntry } from '../types';
import './TerritorySelector.css';

interface TerritorySelectorProps {
  onTerritorySelect: (territories: FUNAITerritoryEntry[]) => void;
  selectedTerritories: FUNAITerritoryEntry[];
  isLoading?: boolean;
}

export const TerritorySelector: React.FC<TerritorySelectorProps> = ({
  onTerritorySelect,
  selectedTerritories,
  isLoading = false
}) => {
  const [territories, setTerritories] = useState<FUNAITerritoryEntry[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [phases, setPhases] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedPhase, setSelectedPhase] = useState<string>('');
  const [indexLoading, setIndexLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load territory index on mount
  useEffect(() => {
    const loadIndex = async () => {
      try {
        const response = await fetch('/data/funai/territories/_INDEX.json');
        if (!response.ok) throw new Error('Failed to load territory index');
        const data: FUNAITerritoryIndex = await response.json();
        setTerritories(data.files);
        setStates(data.states || []);
        setPhases(data.phases || []);
        setError(null);
      } catch (err) {
        console.error('Failed to load territory index:', err);
        setError('Failed to load territories');
      } finally {
        setIndexLoading(false);
      }
    };
    loadIndex();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter territories based on search, state, and phase
  const filteredTerritories = useMemo(() => {
    let filtered = territories;

    // Filter by state
    if (selectedState) {
      filtered = filtered.filter(t =>
        t.state?.split(',').some(s => s.trim() === selectedState)
      );
    }

    // Filter by phase
    if (selectedPhase) {
      filtered = filtered.filter(t => t.phase === selectedPhase);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(term) ||
        t.code.toString().includes(term)
      );
    }

    return filtered;
  }, [territories, searchTerm, selectedState, selectedPhase]);

  // Check if a territory is selected
  const isSelected = (territory: FUNAITerritoryEntry) => {
    return selectedTerritories.some(t => t.code === territory.code);
  };

  // Toggle single territory selection
  const handleToggleTerritory = (territory: FUNAITerritoryEntry) => {
    if (isSelected(territory)) {
      onTerritorySelect(selectedTerritories.filter(t => t.code !== territory.code));
    } else {
      onTerritorySelect([...selectedTerritories, territory]);
    }
  };

  // Toggle all filtered territories
  const handleToggleAll = () => {
    const allFilteredSelected = filteredTerritories.every(t => isSelected(t));

    if (allFilteredSelected) {
      // Deselect all filtered territories
      const filteredCodes = new Set(filteredTerritories.map(t => t.code));
      onTerritorySelect(selectedTerritories.filter(t => !filteredCodes.has(t.code)));
    } else {
      // Select all filtered territories
      const currentCodes = new Set(selectedTerritories.map(t => t.code));
      const newTerritories = filteredTerritories.filter(t => !currentCodes.has(t.code));
      onTerritorySelect([...selectedTerritories, ...newTerritories]);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTerritorySelect([]);
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedState('');
    setSelectedPhase('');
  };

  const activeFiltersCount = [selectedState, selectedPhase, searchTerm].filter(Boolean).length;
  const allFilteredSelected = filteredTerritories.length > 0 && filteredTerritories.every(t => isSelected(t));
  const someFilteredSelected = filteredTerritories.some(t => isSelected(t));

  if (indexLoading) {
    return (
      <div className="territory-selector territory-selector-loading">
        <Loader2 size={16} className="spinner" />
        <span>Loading territories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="territory-selector territory-selector-error">
        <MapPin size={16} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="territory-selector" ref={dropdownRef}>
      <button
        className={`territory-selector-trigger ${isOpen ? 'open' : ''} ${selectedTerritories.length > 0 ? 'has-selection' : ''}`}
        onClick={handleToggle}
        disabled={isLoading}
      >
        <MapPin size={16} className="trigger-icon" />
        <span className="trigger-text">
          {isLoading ? (
            <>
              <Loader2 size={14} className="spinner" />
              Loading...
            </>
          ) : selectedTerritories.length > 0 ? (
            <>
              <span className="selection-count">{selectedTerritories.length}</span>
              {selectedTerritories.length === 1
                ? selectedTerritories[0].name
                : `${selectedTerritories.length} territories selected`}
            </>
          ) : (
            'Select Territories'
          )}
        </span>
        {selectedTerritories.length > 0 && !isLoading ? (
          <X size={16} className="clear-icon" onClick={handleClear} />
        ) : (
          <ChevronDown size={16} className={`chevron ${isOpen ? 'rotated' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="territory-dropdown">
          <div className="dropdown-filters">
            <div className="dropdown-search">
              <Search size={16} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="search-clear" onClick={() => setSearchTerm('')}>
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="dropdown-filter-row">
              <div className="dropdown-state-filter">
                <Filter size={14} />
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="state-select"
                >
                  <option value="">All States ({states.length})</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="dropdown-phase-filter">
                <Scale size={14} />
                <select
                  value={selectedPhase}
                  onChange={(e) => setSelectedPhase(e.target.value)}
                  className="phase-select"
                >
                  <option value="">All Phases ({phases.length})</option>
                  {phases.map(phase => (
                    <option key={phase} value={phase}>{phase}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="dropdown-count">
            <button
              className={`toggle-all-btn ${allFilteredSelected ? 'all-selected' : someFilteredSelected ? 'some-selected' : ''}`}
              onClick={handleToggleAll}
              title={allFilteredSelected ? 'Deselect all filtered' : 'Select all filtered'}
            >
              {allFilteredSelected ? (
                <CheckSquare size={14} />
              ) : (
                <Square size={14} />
              )}
              <span>{allFilteredSelected ? 'Deselect All' : 'Select All'}</span>
            </button>
            <span className="count-text">
              {selectedTerritories.length > 0 && (
                <span className="selected-count">{selectedTerritories.length} selected · </span>
              )}
              {filteredTerritories.length} of {territories.length}
            </span>
            {activeFiltersCount > 0 && (
              <button className="clear-filters" onClick={handleClearFilters}>
                Clear {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''}
              </button>
            )}
          </div>

          <div className="dropdown-list">
            {filteredTerritories.length === 0 ? (
              <div className="dropdown-empty">No territories found</div>
            ) : (
              filteredTerritories.map((territory) => (
                <button
                  key={territory.code}
                  className={`dropdown-item ${isSelected(territory) ? 'selected' : ''}`}
                  onClick={() => handleToggleTerritory(territory)}
                >
                  <span className="item-checkbox">
                    {isSelected(territory) ? (
                      <CheckSquare size={16} />
                    ) : (
                      <Square size={16} />
                    )}
                  </span>
                  <span className="item-code">{territory.code}</span>
                  <span className="item-name">{territory.name}</span>
                  {territory.phase && (
                    <span className={`item-phase phase-${territory.phase.toLowerCase().replace(/\s+/g, '-')}`}>
                      {territory.phase}
                    </span>
                  )}
                  {territory.state && (
                    <span className="item-state">{territory.state}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
